/**
 * How generously a control should grab, given what is pointing at it (#40).
 *
 * Every hit tolerance in the app was tuned for a cursor or a stylus tip — the
 * transform knob grabs within 3.2 screen px, which is a target about 6 px
 * across. That is fine when you are aiming with a mouse and a hover cursor
 * confirms the aim before you press. A fingertip has neither: the contact is
 * roughly 9 mm of skin reported as one point, you cannot see what is under it,
 * and there is no hover to check first.
 *
 * So the tolerance is chosen per event from `pointerType`, which is the honest
 * signal and needs no mode, no setting and no device detection. A stylus is
 * precise and stays on the fine scale; only a finger widens.
 *
 * This scales the GRAB radius, never the drawn handle — the handles stay the
 * size they are, and a finger simply gets more slack around them. Growing the
 * drawn size would change the look of the app for everyone.
 */

export type PointerKind = "mouse" | "pen" | "touch";

/**
 * How much wider a coarse pointer grabs. Raised from 3 to 4 after testing on
 * an iPad: 3× was usable but still wanted aim, and the corner handles had
 * headroom — a small shape's adjacent corners did not compete at 3×, and
 * `touch-handle-targets.spec.ts` holds that line at 4×.
 *
 * Still deliberately not the 44 pt Apple asks for an isolated control: these
 * handles sit in clusters, and past a point widening one only steals from its
 * neighbour rather than making anything easier to hit.
 */
export const COARSE_HIT_SCALE = 4;

/**
 * How much wider a coarse pointer's SNAPS reach (#1150): drawing snaps to
 * vertices and the grid, and the Trace tool's points. Gentler than
 * `COARSE_HIT_SCALE`, because a snap acts on its own — the point jumps to the
 * candidate whether or not the user meant it — so one that reaches too far pulls
 * to the wrong candidate, which is worse than a small miss. A pick (a handle, a
 * stitch line, an edge) only acts on what was aimed at, so it can take the full
 * scale.
 */
export const COARSE_SNAP_SCALE = 2.5;

/** A finger is coarse; a stylus tip and a cursor are not. */
export function isCoarsePointer(kind: PointerKind | undefined): boolean {
  return kind === "touch";
}

/** Scale a fine-pointer hit radius for whatever is actually pointing. */
export function hitRadiusFor(
  fineRadius: number,
  kind: PointerKind | undefined,
): number {
  return isCoarsePointer(kind) ? fineRadius * COARSE_HIT_SCALE : fineRadius;
}

/**
 * The least a finger's grab reaches around a DOT, in screen px (#1150) — about
 * half a fingertip. An anchor dot is sized on the drawing (about 1 mm), so at
 * low zoom it is a couple of pixels across and even 4× that is under the
 * finger; this floor keeps it grabbable there.
 */
export const COARSE_MIN_DOT_HIT_PX = 16;

/** Grab radius, in project mm, for a dot of `fineMm` at view `zoom`: the fine
 *  radius for a mouse or pen; for a finger `COARSE_HIT_SCALE`× it, and never
 *  less than `COARSE_MIN_DOT_HIT_PX` on screen. */
export function dotHitRadiusMm(
  fineMm: number,
  zoom: number,
  kind: PointerKind | undefined,
): number {
  if (!isCoarsePointer(kind)) return fineMm;
  return Math.max(
    fineMm * COARSE_HIT_SCALE,
    COARSE_MIN_DOT_HIT_PX / Math.max(zoom, 1e-4),
  );
}

/** Scale a fine-pointer SNAP radius for whatever is actually pointing. */
export function snapRadiusFor(
  fineRadius: number,
  kind: PointerKind | undefined,
): number {
  return isCoarsePointer(kind) ? fineRadius * COARSE_SNAP_SCALE : fineRadius;
}

let current: PointerKind | undefined;

/**
 * The pointer behind the event being handled right now (#1150). `PointerInput`
 * records it as each pointer event arrives, before any tool sees the event, and
 * tool dispatch is synchronous — so a tolerance computed anywhere under a
 * pointer handler reads the same per-event `pointerType` it would have been
 * passed, without threading it through every hit-test signature. Outside a
 * pointer event (a keypress, a panel edit) it is the last pointer used.
 */
export function currentPointerKind(): PointerKind | undefined {
  return current;
}

/** Called by `PointerInput` for every pointer event. */
export function notePointerKind(kind: PointerKind): void {
  current = kind;
}

/**
 * Whether this pointer hovers — i.e. whether it can show you what a press
 * would do BEFORE you commit to it.
 *
 * Only a mouse does. A finger has no hover at all, and a stylus has none on
 * most tablets, so any interaction whose press acts on hovered state is
 * pressing blind. Two things follow, and both read this predicate:
 *
 *  - `PointerInput` delivers a synthetic hover immediately before a non-mouse
 *    press, so the state a cursor would have built exists by the time the
 *    press lands.
 *  - Tools that COMMIT on press defer that commit to the release instead, so
 *    the press previews, the drag adjusts, and lifting decides. A mouse keeps
 *    hover-then-click, because it already had its look.
 */
export function hasHover(kind: PointerKind | undefined): boolean {
  return kind === "mouse" || kind === undefined;
}
